import React, { useState, useEffect } from 'react';
import { useNavigate, Navigate } from 'react-router-dom';
import { useAdmin } from '../context/AdminContext';
import { useAuth } from '../context/AuthContext';
import { logout as logoutApi } from '../services/api';

function AdminLayout({ children, currentPage = 'dashboard' }) {
  const navigate = useNavigate();
  const {
    isLoggedIn: isAdminLoggedIn,
    showLogin,
    username,
    setUsername,
    password,
    setPassword,
    loginError,
    handleLogin,
    handleLogout: contextLogout,
    theme,
    sidebarActive,
    setSidebarActive,
    toggleTheme,
    selectedDepartment,
    setSelectedDepartment,
    searchTerm,
    setSearchTerm
  } = useAdmin();

  const { logout: authLogout } = useAuth();

  const handleLogout = async () => {
    try {
      await logoutApi();
    } catch (error) {
      console.error("Admin logout API failed:", error);
    }
    contextLogout();
    authLogout();
    navigate('/');
  };

  // Use admin authentication for admin routes
  if (showLogin) {
    return <Navigate to="/" replace />;
  }

  const handleDepartmentClick = (department) => {
    setSelectedDepartment(department);
    navigate(`/admin/admindashboard?department=${encodeURIComponent(department)}`);
    if (window.innerWidth < 768) {
      setSidebarActive(false);
    }
  };

  const handleNavClick = (path) => {
    navigate(path);
    if (window.innerWidth < 768) {
      setSidebarActive(false);
    }
  };

  return (
    <div className="flex h-screen overflow-hidden">
      <link rel="stylesheet" href="https://cdnjs.cloudflare.com/ajax/libs/font-awesome/6.4.0/css/all.min.css" />

      {/* Sidebar Overlay */}
      {sidebarActive && (
        <div 
          className="fixed inset-0 bg-black/40 backdrop-blur-sm z-[999] md:hidden transition-all duration-300"
          onClick={() => setSidebarActive(false)}
        ></div>
      )}

      {/* Sidebar */}
      <aside className={`w-[255px] max-w-[85vw] bg-white dark:bg-gray-800 flex flex-col p-3.5 transition-all duration-300 shadow-[2px_0_10px_rgba(0,0,0,0.08)] fixed md:relative h-screen z-[1000] ${sidebarActive ? 'left-0' : '-left-[100%] md:left-0'}`}>
        <div className="flex items-center justify-between mb-3.5 pb-3 border-b border-gray-200 dark:border-gray-700 shrink-0">
          <div className="flex items-center gap-2.5">
            <div className="w-9 h-9 rounded-lg flex items-center justify-center text-white shrink-0">
              <img
                src="/companylogo.png"
                alt="E-Learning Hub Logo"
                className="w-9 h-9 object-contain"
              />
            </div>
            <h2 className="text-base font-bold text-gray-800 dark:text-gray-100 leading-tight">E-Learning Hub</h2>
          </div>
          <button 
            onClick={() => setSidebarActive(false)}
            className="hidden max-md:flex items-center justify-center w-7 h-7 text-gray-500 hover:text-red-500 transition-colors shrink-0"
          >
            <i className="fa-solid fa-times text-lg"></i>
          </button>
        </div>

        <nav className="flex-1 overflow-y-auto pr-1 -mr-1 hide-scrollbar space-y-3.5">
          <div>
            <div className="text-[11px] uppercase tracking-wider text-gray-400 dark:text-gray-400 mb-1 px-2 font-bold">Main</div>
            <button 
              onClick={() => handleNavClick('/admin/admindashboard')} 
              className={`w-full text-left flex items-center gap-2.5 px-3 py-2 rounded-md transition-all text-xs md:text-sm font-medium mb-0.5 ${currentPage === 'dashboard' ? 'bg-[#00b26f] text-white font-semibold shadow-sm' : 'hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-300'}`}
            >
              <i className="fa-solid fa-chart-line w-4 text-center text-xs md:text-sm"></i>
              <span>Dashboard</span>
            </button>
          </div>

          <div>
            <div className="text-[11px] uppercase tracking-wider text-gray-400 dark:text-gray-400 mb-1 px-2 font-bold">Courses</div>
            <details open>
              <summary className="list-none cursor-pointer px-3 py-2 text-xs md:text-sm font-medium transition-all flex items-center gap-2.5 rounded-md hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-300 select-none">
                <i className="fa-solid fa-book w-4 text-center text-xs md:text-sm"></i>
                <span>Our Courses</span>
              </summary>
              <div className="pl-3.5 pt-1 space-y-0.5 animate-[slideDown_0.2s_ease]">
                <button
                  onClick={() => handleDepartmentClick('all')}
                  className={`w-full text-left flex items-center gap-2 px-2.5 py-1.5 rounded-md transition-all text-xs font-medium ${selectedDepartment === 'all' ? 'bg-[#00b26f] text-white font-semibold' : 'hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-600 dark:text-gray-300'}`}
                >
                  <i className="fa-solid fa-layer-group w-4 text-center text-xs"></i>
                  <span>All Department</span>
                </button>
                <button
                  onClick={() => handleDepartmentClick('Electrical Vehicle')}
                  className={`w-full text-left flex items-center gap-2 px-2.5 py-1.5 rounded-md transition-all text-xs font-medium ${selectedDepartment === 'Electrical Vehicle' ? 'bg-[#00b26f] text-white font-semibold' : 'hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-600 dark:text-gray-300'}`}
                >
                  <i className="fa-solid fa-bolt w-4 text-center text-xs"></i>
                  <span className="truncate">ELECTRICAL VEHICLE (E.V)</span>
                </button>
                <button
                  onClick={() => handleDepartmentClick('IT & ECE')}
                  className={`w-full text-left flex items-center gap-2 px-2.5 py-1.5 rounded-md transition-all text-xs font-medium ${selectedDepartment === 'IT & ECE' ? 'bg-[#00b26f] text-white font-semibold' : 'hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-600 dark:text-gray-300'}`}
                >
                  <i className="fa-solid fa-laptop-code w-4 text-center text-xs"></i>
                  <span>IT & ECE</span>
                </button>
                <button
                  onClick={() => handleDepartmentClick('Mechanical Engineering')}
                  className={`w-full text-left flex items-center gap-2 px-2.5 py-1.5 rounded-md transition-all text-xs font-medium ${selectedDepartment === 'Mechanical Engineering' ? 'bg-[#00b26f] text-white font-semibold' : 'hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-600 dark:text-gray-300'}`}
                >
                  <i className="fa-solid fa-cog w-4 text-center text-xs"></i>
                  <span>Mechanical</span>
                </button>
                <button
                  onClick={() => handleDepartmentClick('Civil Engineering')}
                  className={`w-full text-left flex items-center gap-2 px-2.5 py-1.5 rounded-md transition-all text-xs font-medium ${selectedDepartment === 'Civil Engineering' ? 'bg-[#00b26f] text-white font-semibold' : 'hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-600 dark:text-gray-300'}`}
                >
                  <i className="fa-solid fa-city w-4 text-center text-xs"></i>
                  <span>Civil</span>
                </button>
                <button
                  onClick={() => handleDepartmentClick('Non-Destructive Testing')}
                  className={`w-full text-left flex items-center gap-2 px-2.5 py-1.5 rounded-md transition-all text-xs font-medium ${selectedDepartment === 'Non-Destructive Testing' ? 'bg-[#00b26f] text-white font-semibold' : 'hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-600 dark:text-gray-300'}`}
                >
                  <i className="fa-solid fa-search w-4 text-center text-xs"></i>
                  <span className="truncate">Non-Destructive Testing (NDT)</span>
                </button>
                <button
                  onClick={() => handleDepartmentClick('Destructive Testing')}
                  className={`w-full text-left flex items-center gap-2 px-2.5 py-1.5 rounded-md transition-all text-xs font-medium ${selectedDepartment === 'Destructive Testing' ? 'bg-[#00b26f] text-white font-semibold' : 'hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-600 dark:text-gray-300'}`}
                >
                  <i className="fa-solid fa-search w-4 text-center text-xs"></i>
                  <span className="truncate">Destructive Testing (DT)</span>
                </button>
              </div>
            </details>
          </div>

          <div>
            <div className="text-[11px] uppercase tracking-wider text-gray-400 dark:text-gray-400 mb-1 px-2 font-bold">Management</div>
            <button 
              onClick={() => handleNavClick('/admin/ManagementStudents')} 
              className={`w-full text-left flex items-center gap-2.5 px-3 py-2 rounded-md transition-all text-xs md:text-sm font-medium mb-0.5 ${currentPage === 'students' ? 'bg-[#00b26f] text-white font-semibold shadow-sm' : 'hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-300'}`}
            >
              <i className="fa-solid fa-users w-4 text-center text-xs md:text-sm"></i>
              <span>Student Management</span>
            </button>
            <button 
              onClick={() => handleNavClick('/admin/visitors')} 
              className={`w-full text-left flex items-center gap-2.5 px-3 py-2 rounded-md transition-all text-xs md:text-sm font-medium mb-0.5 ${currentPage === 'visitors' ? 'bg-[#00b26f] text-white font-semibold shadow-sm' : 'hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-300'}`}
            >
              <i className="fa-solid fa-chart-line w-4 text-center text-xs md:text-sm"></i>
              <span>Visitor Analytics</span>
            </button>
            <button 
              onClick={() => handleNavClick('/admin/instructors')} 
              className={`w-full text-left flex items-center gap-2.5 px-3 py-2 rounded-md transition-all text-xs md:text-sm font-medium mb-0.5 ${currentPage === 'instructors' ? 'bg-[#00b26f] text-white font-semibold shadow-sm' : 'hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-300'}`}
            >
              <i className="fa-solid fa-chalkboard-user w-4 text-center text-xs md:text-sm text-emerald-500"></i>
              <span>Become an Instructor</span>
            </button>
            <button 
              onClick={() => handleNavClick('/admin/certificates')} 
              className={`w-full text-left flex items-center gap-2.5 px-3 py-2 rounded-md transition-all text-xs md:text-sm font-medium mb-0.5 ${currentPage === 'certificates' ? 'bg-[#00b26f] text-white font-semibold shadow-sm' : 'hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-300'}`}
            >
              <i className="fa-solid fa-award w-4 text-center text-xs md:text-sm text-amber-500"></i>
              <span>Certificate Requests</span>
            </button>
            <button 
              onClick={() => handleNavClick('/admin/contact-messages')} 
              className={`w-full text-left flex items-center gap-2.5 px-3 py-2 rounded-md transition-all text-xs md:text-sm font-medium mb-0.5 ${currentPage === 'contact-messages' ? 'bg-[#00b26f] text-white font-semibold shadow-sm' : 'hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-300'}`}
            >
              <i className="fa-solid fa-envelope w-4 text-center text-xs md:text-sm text-sky-500"></i>
              <span>Contact Messages</span>
            </button>
            <button 
              onClick={() => handleNavClick('/admin/flash-sale')} 
              className={`w-full text-left flex items-center gap-2.5 px-3 py-2 rounded-md transition-all text-xs md:text-sm font-medium mb-0.5 ${currentPage === 'flash-sale' ? 'bg-[#00b26f] text-white font-semibold shadow-sm' : 'hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-300'}`}
            >
              <i className="fa-solid fa-bolt w-4 text-center text-xs md:text-sm text-amber-500"></i>
              <span>E-Learn Flash Sale!</span>
            </button>
          </div>
        </nav>

        <div className="pt-2.5 border-t border-gray-200 dark:border-gray-700 shrink-0 space-y-0.5">
          <button 
            onClick={() => handleNavClick('/admin/settings')} 
            className="w-full text-left flex items-center gap-2.5 px-3 py-2 rounded-md transition-all text-xs md:text-sm font-medium hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-300"
          >
            <i className="fa-solid fa-gear w-4 text-center text-xs md:text-sm"></i>
            <span>Settings</span>
          </button>
          <button 
            onClick={handleLogout} 
            className="w-full text-left flex items-center gap-2.5 px-3 py-2 rounded-md transition-all text-xs md:text-sm font-medium hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-300"
          >
            <i className="fa-solid fa-sign-out-alt w-4 text-center text-xs md:text-sm"></i>
            <span>Logout</span>
          </button>
          <button 
            onClick={toggleTheme} 
            className="w-full bg-gray-100 dark:bg-gray-700 text-xs font-medium px-3 py-2 rounded-md transition-all flex items-center justify-center gap-2 mt-1 hover:bg-[#00b26f] hover:text-white text-gray-700 dark:text-gray-300"
          >
            <i className={`fa-solid ${theme === 'dark' ? 'fa-sun' : 'fa-moon'} text-xs`}></i>
            <span>{theme === 'dark' ? 'Light Mode' : 'Dark Mode'}</span>
          </button>
        </div>
      </aside>

      {/* Main Content */}
      <main className="flex-1 overflow-y-auto bg-gray-50 dark:bg-gray-900 min-h-screen">
        <header className="bg-white/80 dark:bg-gray-800/80 backdrop-blur-md p-4 md:p-6 flex flex-col md:flex-row items-start md:items-center justify-between shadow-sm sticky top-0 z-50 gap-4 border-b border-gray-100 dark:border-gray-700">
          <div className="flex items-center justify-between w-full md:w-auto gap-4">
            <div className="flex items-center gap-3">
              <button 
                onClick={() => setSidebarActive(!sidebarActive)} 
                className="md:hidden bg-gray-50 dark:bg-gray-700 border-none text-xl cursor-pointer p-2 rounded-lg transition-all hover:bg-[#00b26f] hover:text-white"
              >
                <i className={`fa-solid ${sidebarActive ? 'fa-xmark' : 'fa-bars'}`}></i>
              </button>
              <h1 className="text-xl md:text-3xl font-bold text-gray-800 dark:text-gray-100 truncate max-w-[200px] md:max-w-none">
                {currentPage === 'dashboard' ? 'Dashboard' : currentPage === 'students' ? 'Students' : currentPage === 'courses' ? 'Courses' : currentPage === 'visitors' ? 'Visitor Analytics' : currentPage === 'contact-messages' ? 'Contact Messages' : currentPage === 'flash-sale' ? 'Flash Sale' : 'Admin'}
              </h1>
            </div>
            
            {/* Mobile Actions (Visible only on small screens next to title) */}
            <div className="flex items-center gap-2 md:hidden">
              <button className="bg-gray-50 dark:bg-gray-700 p-2 rounded-full text-gray-700 dark:text-gray-300">
                <i className="fa-solid fa-bell"></i>
              </button>
              <img src="https://ui-avatars.com/api/?name=Admin&background=00b26f&color=fff" alt="Admin" className="w-8 h-8 rounded-full border border-[#00b26f]" />
            </div>
          </div>

          <div className="flex items-center gap-4 w-full md:w-auto">
            <div className="relative flex-1 md:flex-initial">
              <i className="fa-solid fa-search absolute left-4 top-1/2 -translate-y-1/2 text-gray-400"></i>
              <input
                type="text"
                placeholder="Search..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full md:w-[300px] pl-11 pr-4 py-2.5 border-2 border-gray-100 dark:border-gray-700 rounded-full bg-gray-50 dark:bg-gray-800 text-sm transition-all focus:outline-none focus:border-[#00b26f] focus:bg-white dark:focus:bg-gray-700 text-gray-900 dark:text-gray-100"
              />
            </div>
            
            <div className="hidden md:flex items-center gap-4">
              <button className="relative bg-gray-50 dark:bg-gray-700 border-none text-xl cursor-pointer px-3.5 py-2.5 rounded-full transition-all hover:bg-[#00b26f] hover:text-white hover:scale-110 text-gray-700 dark:text-gray-300">
                <i className="fa-solid fa-bell"></i>
                <span className="absolute top-1 right-1 bg-red-600 text-white text-[0.65rem] px-1.5 py-0.5 rounded-xl font-semibold">5</span>
              </button>
              <div className="flex items-center gap-2.5 px-4 py-2 bg-gray-50 dark:bg-gray-700 rounded-full cursor-pointer transition-all hover:bg-[#00b26f] hover:text-white text-gray-700 dark:text-gray-300 border border-transparent hover:border-[#00b26f]">
                <img src="https://ui-avatars.com/api/?name=Admin&background=00b26f&color=fff" alt="Admin" className="w-9 h-9 rounded-full border-2 border-[#00b26f]" />
                <span className="font-semibold text-sm">Admin</span>
              </div>
            </div>
          </div>
        </header>

        {children}
      </main>
    </div>
  );
}

export default AdminLayout;